import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { BookOpen, Check, ExternalLink, GripVertical, ListOrdered, Plus, RefreshCw, Save, Search, Trash2, X } from "lucide-react";
import { api } from "../../services/api";
import partBookData from "../../data/extracted_table_data.json";
import { InsightCard, PaginationControls, StatusPill } from "./AdminPanelParts";
import { ToastStack, useToast } from "./useToast";
import { usePagination } from "./usePagination";

const emptyLink = {
  title: "",
  url: "",
  sourceUrl: "",
};

const emptyPartBook = {
  series: "",
  model: "",
  modelLinks: [{ ...emptyLink }],
  description: "",
  date: "",
  pages: 0,
  fileSize: "",
  active: true,
};

function normalizePartBook(record = {}, index = 0) {
  const modelLinks = record.modelLinks || record.model_links || [];
  return {
    ...record,
    _id: record._id || `preview-${index}`,
    fileSize: record.fileSize || record.file_size || "",
    sortOrder: Number(record.sortOrder || record.sort_order || index + 1),
    modelLinks: modelLinks.map((link) => ({
      title: link.title || "",
      url: link.url || "",
      sourceUrl: link.sourceUrl || link.source_url || "",
    })),
    active: record.active !== false,
  };
}

const fallbackPartBooks = (partBookData.records || []).map(normalizePartBook);
const dragScrollEdgeSize = 150;
const dragScrollMaxStep = 38;
const sortRowHeight = 72;
const sortVirtualBufferRows = 10;

function getDragScrollContainer(element) {
  let current = element?.parentElement;

  while (current) {
    const { overflowY } = window.getComputedStyle(current);
    if ((overflowY === "auto" || overflowY === "scroll") && current.scrollHeight > current.clientHeight) {
      return current;
    }
    current = current.parentElement;
  }

  return document.scrollingElement || document.documentElement;
}

function partBookToForm(partBook = emptyPartBook) {
  const modelLinks = (partBook.modelLinks || partBook.model_links || []).map((link) => ({
    title: link.title || "",
    url: link.url || "",
    sourceUrl: link.sourceUrl || link.source_url || "",
  }));

  return {
    series: partBook.series || "",
    model: partBook.model || "",
    modelLinks: modelLinks.length ? modelLinks : [{ ...emptyLink }],
    description: partBook.description || "",
    date: partBook.date || "",
    pages: partBook.pages || 0,
    fileSize: partBook.fileSize || partBook.file_size || "",
    active: partBook.active !== false,
  };
}

function buildPayload(form) {
  return {
    series: form.series.trim(),
    model: form.model.trim(),
    description: form.description.trim(),
    date: form.date.trim(),
    pages: Number(form.pages || 0),
    fileSize: form.fileSize.trim(),
    active: form.active !== false,
    modelLinks: form.modelLinks
      .filter((link) => link.url.trim())
      .map((link) => ({
        title: link.title.trim() || link.url.trim(),
        url: link.url.trim(),
        sourceUrl: link.sourceUrl.trim(),
      })),
  };
}

function searchableText(partBook) {
  return [
    partBook.series,
    partBook.model,
    partBook.description,
    partBook.date,
    partBook.fileSize,
    ...(partBook.modelLinks || []).flatMap((link) => [link.title, link.url, link.sourceUrl]),
  ]
    .join(" ")
    .toLowerCase();
}

function getEffectiveDropPlacement(indexById, dragId, targetId, placement) {
  const draggedIndex = indexById.get(dragId);
  const targetIndex = indexById.get(targetId);

  if (draggedIndex === undefined || targetIndex === undefined) return placement;
  if (targetIndex === draggedIndex + 1 && placement === "before") return "after";
  if (targetIndex === draggedIndex - 1 && placement === "after") return "before";
  return placement;
}

export default function PartBookAdmin() {
  const [partBooks, setPartBooks] = useState(fallbackPartBooks);
  const [selectedId, setSelectedId] = useState(null);
  const [form, setForm] = useState(emptyPartBook);
  const [formOpen, setFormOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [sortingMode, setSortingMode] = useState(false);
  const [preparingSortMode, setPreparingSortMode] = useState(false);
  const [sortSnapshot, setSortSnapshot] = useState(null);
  const [draggingId, setDraggingId] = useState(null);
  const [dropTargetId, setDropTargetId] = useState(null);
  const [dropPlacement, setDropPlacement] = useState("before");
  const [recentlyDraggedId, setRecentlyDraggedId] = useState(null);
  const [savingOrder, setSavingOrder] = useState(false);
  const [sortViewport, setSortViewport] = useState({
    end: 0,
    start: 0,
  });
  const sortBodyRef = useRef(null);
  const dropIndicatorRef = useRef({
    placement: "before",
    targetId: null,
  });
  const activeDragIdRef = useRef(null);
  const pendingDropIndicatorRef = useRef(null);
  const dropIndicatorFrameRef = useRef(null);
  const sortModeFrameRef = useRef(null);
  const dragScrollRef = useRef({
    container: null,
    frameId: null,
    step: 0,
  });
  const { dismiss, toast, toasts } = useToast();

  const loadPartBooks = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.get("/part-books/admin?limit=1000");
      const nextPartBooks = (data.partBooks || []).map(normalizePartBook);
      setPartBooks(nextPartBooks.length ? nextPartBooks : fallbackPartBooks);
      setSortingMode(false);
      setPreparingSortMode(false);
      setSortSnapshot(null);
      activeDragIdRef.current = null;
      setDraggingId(null);
      dropIndicatorRef.current = { placement: "before", targetId: null };
      setDropTargetId(null);
      setRecentlyDraggedId(null);
    } catch (err) {
      setPartBooks(fallbackPartBooks);
      toast(`Using bundled part-book data: ${err.message}`, "warning");
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    loadPartBooks();
  }, [loadPartBooks]);

  useEffect(() => {
    if (!formOpen) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [formOpen]);

  useEffect(() => {
    if (!recentlyDraggedId) return undefined;
    const timeoutId = window.setTimeout(() => setRecentlyDraggedId(null), 1600);
    return () => window.clearTimeout(timeoutId);
  }, [recentlyDraggedId]);

  const setDropIndicator = useCallback((targetId = null, placement = "before") => {
    const current = dropIndicatorRef.current;
    if (current.targetId === targetId && current.placement === placement) return;

    dropIndicatorRef.current = { placement, targetId };
    setDropTargetId(targetId);
    setDropPlacement(placement);
  }, []);

  const scheduleDropIndicator = useCallback((targetId, placement) => {
    pendingDropIndicatorRef.current = { placement, targetId };
    if (dropIndicatorFrameRef.current) return;

    dropIndicatorFrameRef.current = window.requestAnimationFrame(() => {
      const next = pendingDropIndicatorRef.current;
      dropIndicatorFrameRef.current = null;
      pendingDropIndicatorRef.current = null;
      if (next) setDropIndicator(next.targetId, next.placement);
    });
  }, [setDropIndicator]);

  const clearDropIndicator = useCallback(() => {
    if (dropIndicatorFrameRef.current) {
      window.cancelAnimationFrame(dropIndicatorFrameRef.current);
    }

    dropIndicatorFrameRef.current = null;
    pendingDropIndicatorRef.current = null;
    setDropIndicator(null, "before");
  }, [setDropIndicator]);

  const stopDragAutoScroll = useCallback(() => {
    if (dragScrollRef.current.frameId) {
      window.cancelAnimationFrame(dragScrollRef.current.frameId);
    }

    dragScrollRef.current.frameId = null;
    dragScrollRef.current.container = null;
    dragScrollRef.current.step = 0;
  }, []);

  const runDragAutoScroll = useCallback(() => {
    const step = dragScrollRef.current.step;
    if (!step) {
      dragScrollRef.current.frameId = null;
      return;
    }

    const container = dragScrollRef.current.container || document.scrollingElement || document.documentElement;

    if (container === document.scrollingElement || container === document.documentElement || container === document.body) {
      window.scrollBy({ top: step, behavior: "auto" });
    } else {
      container.scrollTop += step;
    }

    dragScrollRef.current.frameId = window.requestAnimationFrame(runDragAutoScroll);
  }, []);

  const updateDragAutoScroll = useCallback((clientY) => {
    const container = dragScrollRef.current.container || document.scrollingElement || document.documentElement;
    const isDocumentScroller = container === document.scrollingElement || container === document.documentElement || container === document.body;
    const bounds = isDocumentScroller
      ? { top: 0, bottom: window.innerHeight || document.documentElement.clientHeight }
      : container.getBoundingClientRect();
    let nextStep = 0;

    if (clientY < bounds.top + dragScrollEdgeSize) {
      const strength = (bounds.top + dragScrollEdgeSize - clientY) / dragScrollEdgeSize;
      nextStep = -Math.max(10, Math.round(strength * dragScrollMaxStep));
    } else if (clientY > bounds.bottom - dragScrollEdgeSize) {
      const strength = (clientY - (bounds.bottom - dragScrollEdgeSize)) / dragScrollEdgeSize;
      nextStep = Math.max(10, Math.round(strength * dragScrollMaxStep));
    }

    dragScrollRef.current.step = nextStep;

    if (nextStep && !dragScrollRef.current.frameId) {
      dragScrollRef.current.frameId = window.requestAnimationFrame(runDragAutoScroll);
    } else if (!nextStep) {
      if (dragScrollRef.current.frameId) {
        window.cancelAnimationFrame(dragScrollRef.current.frameId);
      }
      dragScrollRef.current.frameId = null;
      dragScrollRef.current.step = 0;
    }
  }, [runDragAutoScroll]);

  useEffect(() => stopDragAutoScroll, [stopDragAutoScroll]);

  const updateSortViewport = useCallback(() => {
    if (!sortingMode || !sortBodyRef.current) return;

    const container = getDragScrollContainer(sortBodyRef.current);
    const isDocumentScroller = container === document.scrollingElement || container === document.documentElement || container === document.body;
    const containerBounds = isDocumentScroller
      ? { top: 0, bottom: window.innerHeight || document.documentElement.clientHeight }
      : container.getBoundingClientRect();
    const bodyBounds = sortBodyRef.current.getBoundingClientRect();
    const visibleTop = Math.max(0, containerBounds.top - bodyBounds.top);
    const visibleBottom = Math.min(
      partBooks.length * sortRowHeight,
      containerBounds.bottom - bodyBounds.top
    );
    const start = Math.max(0, Math.floor(visibleTop / sortRowHeight) - sortVirtualBufferRows);
    const end = Math.min(
      partBooks.length,
      Math.ceil(visibleBottom / sortRowHeight) + sortVirtualBufferRows
    );

    setSortViewport((current) => (
      current.start === start && current.end === end ? current : { end, start }
    ));
  }, [partBooks.length, sortingMode]);

  useEffect(() => {
    if (!sortingMode) return undefined;

    const body = sortBodyRef.current;
    if (!body) return undefined;

    const container = getDragScrollContainer(body);
    let frameId = null;
    const scheduleUpdate = () => {
      if (frameId) return;
      frameId = window.requestAnimationFrame(() => {
        frameId = null;
        updateSortViewport();
      });
    };

    updateSortViewport();
    container.addEventListener("scroll", scheduleUpdate, { passive: true });
    window.addEventListener("resize", scheduleUpdate);

    return () => {
      if (frameId) window.cancelAnimationFrame(frameId);
      container.removeEventListener("scroll", scheduleUpdate);
      window.removeEventListener("resize", scheduleUpdate);
    };
  }, [sortingMode, updateSortViewport]);

  useEffect(() => {
    return () => {
      if (sortModeFrameRef.current) {
        window.cancelAnimationFrame(sortModeFrameRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!draggingId) return undefined;

    const handleDragOver = (event) => {
      event.preventDefault();
      updateDragAutoScroll(event.clientY);
    };

    const handleDragStop = () => {
      stopDragAutoScroll();
      clearDropIndicator();
    };

    document.addEventListener("dragover", handleDragOver, true);
    document.addEventListener("dragend", handleDragStop, true);

    return () => {
      document.removeEventListener("dragover", handleDragOver, true);
      document.removeEventListener("dragend", handleDragStop, true);
    };
  }, [clearDropIndicator, draggingId, stopDragAutoScroll, updateDragAutoScroll]);

  const selectedPartBook = partBooks.find((partBook) => partBook._id === selectedId);

  const visiblePartBooks = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return partBooks;
    return partBooks.filter((partBook) => searchableText(partBook).includes(term));
  }, [partBooks, search]);

  const pagination = usePagination(visiblePartBooks, [search], 10);
  const tableItems = sortingMode ? partBooks : pagination.paginatedItems;
  const renderedTableItems = sortingMode
    ? partBooks.slice(sortViewport.start, Math.max(sortViewport.end, sortViewport.start + 1))
    : tableItems;
  const virtualPaddingTop = sortingMode ? sortViewport.start * sortRowHeight : 0;
  const virtualPaddingBottom = sortingMode
    ? Math.max(0, (partBooks.length - Math.max(sortViewport.end, sortViewport.start + 1)) * sortRowHeight)
    : 0;
  const tableIndexById = useMemo(
    () => new Map((sortingMode ? partBooks : tableItems).map((partBook, index) => [partBook._id, index])),
    [partBooks, sortingMode, tableItems]
  );

  const metrics = useMemo(() => {
    const active = partBooks.filter((partBook) => partBook.active !== false).length;
    const links = partBooks.reduce((sum, partBook) => sum + (partBook.modelLinks || []).length, 0);
    return {
      total: partBooks.length,
      active,
      hidden: partBooks.length - active,
      links,
    };
  }, [partBooks]);

  const resetForm = () => {
    setSelectedId(null);
    setForm(emptyPartBook);
  };

  const startNew = () => {
    resetForm();
    setFormOpen(true);
  };

  const closeForm = () => {
    setFormOpen(false);
  };

  const startSorting = () => {
    setSearch("");
    setSortSnapshot(partBooks);
    setPreparingSortMode(true);
    setSortViewport({ end: 0, start: 0 });

    if (sortModeFrameRef.current) {
      window.cancelAnimationFrame(sortModeFrameRef.current);
    }

    sortModeFrameRef.current = window.requestAnimationFrame(() => {
      sortModeFrameRef.current = window.requestAnimationFrame(() => {
        setSortingMode(true);
        setPreparingSortMode(false);
        sortModeFrameRef.current = null;
      });
    });
  };

  const cancelSorting = () => {
    if (sortModeFrameRef.current) {
      window.cancelAnimationFrame(sortModeFrameRef.current);
      sortModeFrameRef.current = null;
    }

    if (sortSnapshot) setPartBooks(sortSnapshot);
    setSortSnapshot(null);
    setPreparingSortMode(false);
    setSortViewport({ end: 0, start: 0 });
    activeDragIdRef.current = null;
    setDraggingId(null);
    clearDropIndicator();
    setRecentlyDraggedId(null);
    stopDragAutoScroll();
    setSortingMode(false);
  };

  const movePartBook = (dragId, targetId, placement = "before") => {
    if (!dragId || !targetId || dragId === targetId) return;

    setPartBooks((current) => {
      const draggedIndex = current.findIndex((item) => item._id === dragId);
      const targetIndex = current.findIndex((item) => item._id === targetId);
      if (draggedIndex < 0 || targetIndex < 0) return current;

      const next = [...current];
      const [dragged] = next.splice(draggedIndex, 1);
      const nextTargetIndex = next.findIndex((item) => item._id === targetId);
      const adjacentPlacement = getEffectiveDropPlacement(
        new Map(current.map((item, index) => [item._id, index])),
        dragId,
        targetId,
        placement
      );
      const insertIndex = nextTargetIndex + (adjacentPlacement === "after" ? 1 : 0);
      next.splice(insertIndex, 0, dragged);
      return next.map((item, index) => ({ ...item, sortOrder: index + 1 }));
    });

    setRecentlyDraggedId(dragId);
  };

  const saveSortOrder = async () => {
    const orderedIds = partBooks.map((partBook) => partBook._id);
    if (orderedIds.some((id) => String(id).startsWith("preview-"))) {
      toast("Connect to the database before saving sort order.", "error");
      return;
    }

    setSavingOrder(true);
    try {
      const data = await api.patch("/part-books/reorder", { orderedIds });
      const nextPartBooks = (data.partBooks || []).map(normalizePartBook);
      if (nextPartBooks.length) setPartBooks(nextPartBooks);
      setSortSnapshot(null);
      activeDragIdRef.current = null;
      setDraggingId(null);
      clearDropIndicator();
      setRecentlyDraggedId(null);
      stopDragAutoScroll();
      setSortingMode(false);
      setPreparingSortMode(false);
      setSortViewport({ end: 0, start: 0 });
      toast("PDF library order saved", "success");
    } catch (err) {
      toast(err.message, "error");
    } finally {
      setSavingOrder(false);
    }
  };

  const editPartBook = (partBook) => {
    setSelectedId(partBook._id);
    setForm(partBookToForm(partBook));
    setFormOpen(true);
  };

  const updateForm = (event) => {
    const { checked, name, type, value } = event.target;
    setForm((current) => ({
      ...current,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const updateLink = (index, field, value) => {
    setForm((current) => ({
      ...current,
      modelLinks: current.modelLinks.map((link, linkIndex) =>
        linkIndex === index ? { ...link, [field]: value } : link
      ),
    }));
  };

  const addLink = () => {
    setForm((current) => ({
      ...current,
      modelLinks: [...current.modelLinks, { ...emptyLink }],
    }));
  };

  const removeLink = (index) => {
    setForm((current) => ({
      ...current,
      modelLinks: current.modelLinks.length === 1
        ? [{ ...emptyLink }]
        : current.modelLinks.filter((_, linkIndex) => linkIndex !== index),
    }));
  };

  const savePartBook = async (event) => {
    event.preventDefault();
    try {
      const payload = buildPayload(form);
      const data = selectedPartBook && !String(selectedPartBook._id).startsWith("preview-")
        ? await api.patch(`/part-books/${selectedPartBook._id}`, payload)
        : await api.post("/part-books", payload);

      const saved = normalizePartBook(data.partBook);
      setPartBooks((current) => {
        const existingIndex = current.findIndex((item) => item._id === saved._id || item._id === selectedId);
        if (existingIndex < 0) return [...current, saved];
        return current.map((item, index) => (index === existingIndex ? saved : item));
      });
      setSelectedId(saved._id);
      setForm(partBookToForm(saved));
      setFormOpen(false);
      toast(`${selectedPartBook && !String(selectedPartBook._id).startsWith("preview-") ? "Updated" : "Created"} part book`, "success");
    } catch (err) {
      toast(err.message, "error");
    }
  };

  const deletePartBook = async (partBook) => {
    if (!partBook?._id || String(partBook._id).startsWith("preview-")) return;
    if (!window.confirm(`Delete ${partBook.model}?`)) return;

    try {
      await api.delete(`/part-books/${partBook._id}`);
      setPartBooks((current) => current.filter((item) => item._id !== partBook._id));
      if (selectedId === partBook._id) resetForm();
      toast("Part book deleted", "success");
    } catch (err) {
      toast(err.message, "error");
    }
  };

  return (
    <div className="p-6 md:p-10">
      <ToastStack dismiss={dismiss} toasts={toasts} />

      <div className="mb-8 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <p className="text-sm font-black uppercase tracking-wide text-[#4391d1]">Part Books</p>
          <h1 className="mt-2 text-3xl font-black text-gray-900">PDF library management</h1>
          <p className="mt-2 max-w-3xl text-sm font-semibold text-gray-500">
            Manage the searchable parts book records shown on the public Part Book PDF page.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button className="inline-flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-4 py-2 text-sm font-black text-gray-700 hover:border-[#4391d1] hover:text-[#4391d1]" onClick={loadPartBooks} type="button">
            <RefreshCw size={16} /> Refresh
          </button>
          {sortingMode || preparingSortMode ? (
            <>
              <button className="inline-flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-4 py-2 text-sm font-black text-gray-700 hover:border-[#4391d1] hover:text-[#4391d1]" disabled={savingOrder} onClick={cancelSorting} type="button">
                <X size={16} /> Cancel sorting
              </button>
              <button className="inline-flex items-center gap-2 rounded-xl bg-emerald-600 px-4 py-2 text-sm font-black text-white hover:bg-emerald-700 disabled:opacity-50" disabled={savingOrder || preparingSortMode} onClick={saveSortOrder} type="button">
                <Check size={16} /> {preparingSortMode ? "Preparing list" : savingOrder ? "Saving order" : "Save order"}
              </button>
            </>
          ) : (
            <button className="inline-flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-4 py-2 text-sm font-black text-gray-700 hover:border-[#4391d1] hover:text-[#4391d1]" onClick={startSorting} type="button">
              <ListOrdered size={16} /> Sort order
            </button>
          )}
          <button className="inline-flex items-center gap-2 rounded-xl bg-[#4391d1] px-4 py-2 text-sm font-black text-white hover:bg-blue-600 disabled:opacity-50" disabled={sortingMode || preparingSortMode} onClick={startNew} type="button">
            <Plus size={16} /> New Part Book
          </button>
        </div>
      </div>

      <div className="mb-8 grid gap-5 md:grid-cols-4">
        <InsightCard title="Records" value={metrics.total} note="Database part books" />
        <InsightCard title="Active" value={metrics.active} note="Visible on public page" tone="good" />
        <InsightCard title="PDF Links" value={metrics.links} note="Downloadable files" tone="info" />
        <InsightCard title="Hidden" value={metrics.hidden} note="Inactive records" tone={metrics.hidden ? "warn" : "good"} />
      </div>

      <div className="grid gap-6">
        <section className="overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm">
          <div className="flex flex-col gap-3 border-b border-gray-100 px-6 py-5 md:flex-row md:items-center md:justify-between">
            <div>
              <h2 className="text-lg font-black text-gray-900">Part book records</h2>
              <p className="mt-1 text-xs font-bold uppercase text-gray-400">
                {loading ? "Loading" : preparingSortMode ? "Preparing full sort list" : sortingMode ? `Sorting all ${partBooks.length} records` : `${visiblePartBooks.length} matching records`}
              </p>
            </div>
            {sortingMode || preparingSortMode ? (
              <div className="rounded-xl border border-blue-100 bg-blue-50 px-4 py-3 text-xs font-black uppercase text-blue-700">
                {preparingSortMode ? "Opening complete sort list." : "Drag rows into the exact first-to-last website order."}
              </div>
            ) : (
              <label className="relative w-full md:max-w-sm">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                <input
                  className="w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 pl-10 text-sm font-bold outline-none focus:border-[#4391d1]"
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Search series, model, PDF link..."
                  value={search}
                />
              </label>
            )}
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-400">
                <tr>
                  <th className="px-6 py-4 font-black">Order</th>
                  <th className="px-6 py-4 font-black">Series</th>
                  <th className="px-6 py-4 font-black">Model</th>
                  <th className="px-6 py-4 font-black">PDFs</th>
                  <th className="px-6 py-4 font-black">Date</th>
                  <th className="px-6 py-4 font-black">Pages</th>
                  <th className="px-6 py-4 font-black">File Size</th>
                  <th className="px-6 py-4 font-black">Status</th>
                  <th className="px-6 py-4 font-black">Actions</th>
                </tr>
              </thead>
              <tbody
                className="divide-y divide-gray-100"
                ref={sortBodyRef}
                onDragOver={(event) => {
                  if (!sortingMode || !activeDragIdRef.current) return;
                  event.preventDefault();
                  event.dataTransfer.dropEffect = "move";
                }}
                onDrop={(event) => {
                  if (!sortingMode || !activeDragIdRef.current || !dropIndicatorRef.current.targetId) return;
                  event.preventDefault();
                  movePartBook(
                    activeDragIdRef.current,
                    dropIndicatorRef.current.targetId,
                    dropIndicatorRef.current.placement
                  );
                  activeDragIdRef.current = null;
                  setDraggingId(null);
                  clearDropIndicator();
                  stopDragAutoScroll();
                }}
              >
                {virtualPaddingTop > 0 && (
                  <tr aria-hidden="true">
                    <td colSpan="9" style={{ height: `${virtualPaddingTop}px`, padding: 0 }} />
                  </tr>
                )}
                {renderedTableItems.map((partBook, localIndex) => {
                  const index = sortingMode ? sortViewport.start + localIndex : localIndex;
                  const showBeforeDropSpace = sortingMode && draggingId && draggingId !== partBook._id && dropTargetId === partBook._id && dropPlacement === "before";
                  const showAfterDropSpace = sortingMode && draggingId && draggingId !== partBook._id && dropTargetId === partBook._id && dropPlacement === "after";
                  const isDragging = draggingId === partBook._id;
                  const wasJustDragged = recentlyDraggedId === partBook._id;
                  const rowClassName = [
                    selectedId === partBook._id ? "bg-blue-50/60" : "bg-white",
                    sortingMode ? "cursor-grab transition-colors hover:bg-blue-50/70" : "",
                    isDragging ? "bg-blue-100 text-blue-950 shadow-[inset_4px_0_0_#4391d1]" : "",
                    wasJustDragged ? "bg-emerald-50 shadow-[inset_4px_0_0_#059669]" : "",
                  ].filter(Boolean).join(" ");
                  const dropOnSpacer = (event, placement) => {
                    event.preventDefault();
                    movePartBook(activeDragIdRef.current, partBook._id, placement);
                    activeDragIdRef.current = null;
                    setDraggingId(null);
                    clearDropIndicator();
                    stopDragAutoScroll();
                  };
                  const renderDropSpace = (placement) => (
                    <tr
                      className="bg-blue-50"
                      onDragOver={(event) => {
                        event.preventDefault();
                        event.dataTransfer.dropEffect = "move";
                      }}
                      onDrop={(event) => dropOnSpacer(event, placement)}
                    >
                      <td className="px-6 py-2" colSpan="9">
                        <div className="h-7 rounded-xl border-2 border-dashed border-[#4391d1] bg-white/80" />
                      </td>
                    </tr>
                  );

                  return (
                    <Fragment key={partBook._id}>
                      {showBeforeDropSpace && renderDropSpace("before")}
                      <tr
                        draggable={sortingMode && !String(partBook._id).startsWith("preview-")}
                        className={rowClassName}
                        onDragEnd={() => {
                          activeDragIdRef.current = null;
                          setDraggingId(null);
                          clearDropIndicator();
                          stopDragAutoScroll();
                        }}
                        onDragOver={(event) => {
                          if (!sortingMode) return;
                          event.preventDefault();
                          const rect = event.currentTarget.getBoundingClientRect();
                          const placement = event.clientY > rect.top + rect.height / 2 ? "after" : "before";
                          scheduleDropIndicator(
                            partBook._id,
                            getEffectiveDropPlacement(tableIndexById, activeDragIdRef.current, partBook._id, placement)
                          );
                          event.dataTransfer.dropEffect = "move";
                        }}
                        onDragStart={(event) => {
                          if (!sortingMode) return;
                          dragScrollRef.current.container = getDragScrollContainer(event.currentTarget);
                          activeDragIdRef.current = partBook._id;
                          setDraggingId(partBook._id);
                          clearDropIndicator();
                          setRecentlyDraggedId(null);
                          event.dataTransfer.effectAllowed = "move";
                        }}
                        onDrop={(event) => {
                          if (!sortingMode) return;
                          event.preventDefault();
                          const rect = event.currentTarget.getBoundingClientRect();
                          const placement = event.clientY > rect.top + rect.height / 2 ? "after" : "before";
                          movePartBook(
                            activeDragIdRef.current,
                            partBook._id,
                            getEffectiveDropPlacement(tableIndexById, activeDragIdRef.current, partBook._id, placement)
                          );
                          activeDragIdRef.current = null;
                          setDraggingId(null);
                          clearDropIndicator();
                          stopDragAutoScroll();
                        }}
                      >
                        <td className="w-24 px-6 py-4 align-top">
                          <div className="flex items-center gap-2 text-sm font-black text-gray-500">
                            {sortingMode && <GripVertical className={isDragging ? "text-[#4391d1]" : "text-gray-400"} size={18} />}
                            <span>{sortingMode ? index + 1 : partBook.sortOrder || index + 1}</span>
                          </div>
                        </td>
                        <td className="min-w-56 px-6 py-4 align-top">
                          <div className="font-black text-gray-900">{partBook.series}</div>
                        </td>
                        <td className="min-w-72 px-6 py-4 align-top">
                          <div className="font-bold text-gray-800">{partBook.model}</div>
                          <div className={`mt-1 max-w-md text-xs font-semibold text-gray-500 ${sortingMode ? "truncate" : ""}`}>{partBook.description}</div>
                        </td>
                        <td className="min-w-44 px-6 py-4 align-top">
                          {sortingMode ? (
                            <span className="rounded-lg bg-gray-100 px-2.5 py-1.5 text-xs font-black text-gray-500">
                              {(partBook.modelLinks || []).length} PDF{(partBook.modelLinks || []).length === 1 ? "" : "s"}
                            </span>
                          ) : (
                            <div className="flex flex-wrap gap-2">
                              {(partBook.modelLinks || []).slice(0, 3).map((link) => (
                                <a className="inline-flex items-center gap-1 rounded-lg bg-blue-50 px-2.5 py-1.5 text-xs font-black text-[#4391d1]" href={link.url} key={`${partBook._id}-${link.url}`} rel="noreferrer" target="_blank">
                                  <ExternalLink size={12} /> {link.title || "PDF"}
                                </a>
                              ))}
                              {(partBook.modelLinks || []).length > 3 && (
                                <span className="rounded-lg bg-gray-100 px-2.5 py-1.5 text-xs font-black text-gray-500">+{partBook.modelLinks.length - 3}</span>
                              )}
                            </div>
                          )}
                        </td>
                        <td className="min-w-32 px-6 py-4 align-top text-sm font-bold text-gray-600">
                          {partBook.date || "No date"}
                        </td>
                        <td className="px-6 py-4 align-top text-sm font-bold text-gray-600">
                          {Number(partBook.pages || 0) || "-"}
                        </td>
                        <td className="min-w-32 px-6 py-4 align-top text-sm font-bold text-gray-600">
                          {partBook.fileSize || "No size"}
                        </td>
                        <td className="px-6 py-4 align-top">
                          <StatusPill tone={partBook.active !== false ? "good" : "warn"}>
                            {partBook.active !== false ? "active" : "hidden"}
                          </StatusPill>
                        </td>
                        <td className="px-6 py-4 align-top">
                          {sortingMode ? (
                            <span className="text-xs font-black uppercase text-gray-400">Sorting</span>
                          ) : (
                            <div className="flex flex-wrap gap-2">
                              <button className="rounded-lg border border-gray-200 px-3 py-2 text-xs font-black text-gray-700 hover:border-[#4391d1] hover:text-[#4391d1] disabled:opacity-40" onClick={() => editPartBook(partBook)} type="button">
                                Edit
                              </button>
                              <button className="rounded-lg border border-red-100 px-3 py-2 text-xs font-black text-red-600 hover:bg-red-50 disabled:opacity-40" disabled={String(partBook._id).startsWith("preview-")} onClick={() => deletePartBook(partBook)} type="button">
                                Delete
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                      {showAfterDropSpace && renderDropSpace("after")}
                    </Fragment>
                  );
                })}
                {virtualPaddingBottom > 0 && (
                  <tr aria-hidden="true">
                    <td colSpan="9" style={{ height: `${virtualPaddingBottom}px`, padding: 0 }} />
                  </tr>
                )}
                {!tableItems.length && (
                  <tr>
                    <td className="px-6 py-8 text-center text-sm font-bold text-gray-500" colSpan="9">
                      No part books match the current search.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {!sortingMode && <PaginationControls pagination={pagination} />}
        </section>

      </div>

      {formOpen && (
        <div className="fixed inset-0 z-[1600] flex items-start justify-center overflow-y-auto bg-slate-900/55 px-4 py-6 backdrop-blur-sm md:py-10">
          <form
            aria-labelledby="part-book-form-title"
            aria-modal="true"
            className="w-full max-w-3xl rounded-2xl border border-gray-100 bg-white p-5 shadow-2xl md:p-6"
            onSubmit={savePartBook}
            role="dialog"
          >
            <div className="mb-5 flex items-start justify-between gap-4 border-b border-gray-100 pb-4">
              <div>
                <h2 className="text-lg font-black text-gray-900" id="part-book-form-title">{selectedPartBook ? "Edit part book" : "New part book"}</h2>
                <p className="mt-1 text-xs font-bold uppercase text-gray-400">
                  {selectedPartBook ? selectedPartBook.model : "Create database record"}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-50 text-[#4391d1]">
                  <BookOpen size={18} />
                </span>
                <button className="flex h-10 w-10 items-center justify-center rounded-xl border border-gray-200 text-gray-500 hover:border-[#4391d1] hover:text-[#4391d1]" onClick={closeForm} type="button" aria-label="Close part book form">
                  <X size={18} />
                </button>
              </div>
            </div>

            <div className="max-h-[calc(100vh-220px)] overflow-y-auto pr-1">
              <div className="grid gap-4">
                <label>
                  <span className="mb-2 block text-xs font-black uppercase text-gray-500">Series</span>
                  <input className="w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm font-bold" name="series" onChange={updateForm} required value={form.series} />
                </label>
                <label>
                  <span className="mb-2 block text-xs font-black uppercase text-gray-500">Model</span>
                  <input className="w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm font-bold" name="model" onChange={updateForm} required value={form.model} />
                </label>
                <label>
                  <span className="mb-2 block text-xs font-black uppercase text-gray-500">Description</span>
                  <textarea className="min-h-24 w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm font-bold" name="description" onChange={updateForm} value={form.description} />
                </label>
                <div className="grid gap-4 sm:grid-cols-3">
                  <label>
                    <span className="mb-2 block text-xs font-black uppercase text-gray-500">Date</span>
                    <input className="w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm font-bold" name="date" onChange={updateForm} value={form.date} />
                  </label>
                  <label>
                    <span className="mb-2 block text-xs font-black uppercase text-gray-500">Pages</span>
                    <input className="w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm font-bold" min="0" name="pages" onChange={updateForm} type="number" value={form.pages} />
                  </label>
                  <label>
                    <span className="mb-2 block text-xs font-black uppercase text-gray-500">File size</span>
                    <input className="w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm font-bold" name="fileSize" onChange={updateForm} value={form.fileSize} />
                  </label>
                </div>
                <label className="flex items-center gap-3 rounded-xl border border-gray-100 bg-gray-50 px-4 py-3 text-sm font-black text-gray-700">
                  <input checked={form.active} name="active" onChange={updateForm} type="checkbox" />
                  Show this part book on the public page
                </label>
              </div>

              <div className="mt-6">
                <div className="mb-3 flex items-center justify-between gap-3">
                  <h3 className="text-sm font-black uppercase text-gray-900">PDF links</h3>
                  <button className="inline-flex items-center gap-1 rounded-lg border border-gray-200 px-3 py-2 text-xs font-black text-gray-700 hover:border-[#4391d1] hover:text-[#4391d1]" onClick={addLink} type="button">
                    <Plus size={14} /> Add link
                  </button>
                </div>
                <div className="grid gap-3">
                  {form.modelLinks.map((link, index) => (
                    <div className="rounded-xl border border-gray-100 bg-gray-50 p-3" key={`link-${index}`}>
                      <div className="mb-3 flex items-center justify-between">
                        <span className="text-xs font-black uppercase text-gray-500">Link {index + 1}</span>
                        <button className="rounded-lg p-1.5 text-red-500 hover:bg-red-50" onClick={() => removeLink(index)} type="button">
                          <Trash2 size={15} />
                        </button>
                      </div>
                      <div className="grid gap-3">
                        <input className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-bold" onChange={(event) => updateLink(index, "title", event.target.value)} placeholder="PDF title" value={link.title} />
                        <input className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-bold" onChange={(event) => updateLink(index, "url", event.target.value)} placeholder="/part-books/file.pdf" required={index === 0} value={link.url} />
                        <input className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-bold" onChange={(event) => updateLink(index, "sourceUrl", event.target.value)} placeholder="Original source URL" value={link.sourceUrl} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="mt-6 flex flex-wrap gap-2 border-t border-gray-100 pt-4">
              <button className="inline-flex flex-1 items-center justify-center gap-2 rounded-xl bg-[#4391d1] px-4 py-3 text-sm font-black text-white hover:bg-blue-600" type="submit">
                <Save size={16} /> Save Part Book
              </button>
              <button className="rounded-xl border border-gray-200 px-4 py-3 text-sm font-black text-gray-700 hover:border-[#4391d1] hover:text-[#4391d1]" onClick={closeForm} type="button">
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
